import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import { CheckCircle2, Users, Target, Zap, Shield, TrendingUp, Phone, Mail, MapPin, ChevronDown, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import ShaderBackground from '../components/ui/animated-shader-hero';

// Country codes data
const countryCodes = [
  { code: '+1', country: 'US', name: 'United States' },
  { code: '+1', country: 'CA', name: 'Canada' },
  { code: '+44', country: 'GB', name: 'United Kingdom' },
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+61', country: 'AU', name: 'Australia' },
  { code: '+971', country: 'AE', name: 'United Arab Emirates' },
  { code: '+43', country: 'AT', name: 'Austria' },
  { code: '+880', country: 'BD', name: 'Bangladesh' },
  { code: '+32', country: 'BE', name: 'Belgium' },
  { code: '+55', country: 'BR', name: 'Brazil' },
  { code: '+86', country: 'CN', name: 'China' },
  { code: '+45', country: 'DK', name: 'Denmark' },
  { code: '+20', country: 'EG', name: 'Egypt' },
  { code: '+358', country: 'FI', name: 'Finland' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+852', country: 'HK', name: 'Hong Kong' },
  { code: '+62', country: 'ID', name: 'Indonesia' },
  { code: '+353', country: 'IE', name: 'Ireland' },
  { code: '+39', country: 'IT', name: 'Italy' },
  { code: '+81', country: 'JP', name: 'Japan' },
  { code: '+254', country: 'KE', name: 'Kenya' },
  { code: '+82', country: 'KR', name: 'South Korea' },
  { code: '+60', country: 'MY', name: 'Malaysia' },
  { code: '+52', country: 'MX', name: 'Mexico' },
  { code: '+31', country: 'NL', name: 'Netherlands' },
  { code: '+64', country: 'NZ', name: 'New Zealand' },
  { code: '+234', country: 'NG', name: 'Nigeria' },
  { code: '+47', country: 'NO', name: 'Norway' },
  { code: '+92', country: 'PK', name: 'Pakistan' },
  { code: '+63', country: 'PH', name: 'Philippines' },
  { code: '+48', country: 'PL', name: 'Poland' },
  { code: '+351', country: 'PT', name: 'Portugal' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia' },
  { code: '+65', country: 'SG', name: 'Singapore' },
  { code: '+27', country: 'ZA', name: 'South Africa' },
  { code: '+34', country: 'ES', name: 'Spain' },
  { code: '+46', country: 'SE', name: 'Sweden' },
  { code: '+41', country: 'CH', name: 'Switzerland' },
  { code: '+66', country: 'TH', name: 'Thailand' },
  { code: '+84', country: 'VN', name: 'Vietnam' },
];

const Home = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    businessName: '',
    websiteType: '',
    hasDomain: '',
    hasHosting: '',
    hasContent: '',
    needsLogo: '',
    contactFormType: 'contact',
    needsEcommerce: '',
    needsMaintenance: '',
    referenceUrl: ''
  });

  const [addOns, setAddOns] = useState({
    domain: false,
    hosting: false,
    content: false,
    logo: false,
    bookingForm: false,
    ecommerce: false,
    maintenance: false
  });

  const [totalPrice, setTotalPrice] = useState(199);
  const [showThankYou, setShowThankYou] = useState(false);
  const [countryCodeOpen, setCountryCodeOpen] = useState(false);

  const totalSteps = 10;

  const pricing = {
    base: 199,
    domain: 15,
    hosting: 10,
    content: 10,
    logo: 29,
    bookingForm: 15,
    ecommerce: 99,
    maintenance: 10
  };

  useEffect(() => {
    let total = pricing.base;
    if (addOns.domain) total += pricing.domain;
    if (addOns.hosting) total += pricing.hosting;
    if (addOns.content) total += pricing.content;
    if (addOns.logo) total += pricing.logo;
    if (addOns.bookingForm) total += pricing.bookingForm;
    if (addOns.ecommerce) total += pricing.ecommerce;
    if (addOns.maintenance) total += pricing.maintenance;
    setTotalPrice(total);
  }, [addOns]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('handleNext called, currentStep:', currentStep);
    
    // Validate step 0 - all basic info fields
    if (currentStep === 0) {
      if (!formData.name) {
        toast.error('Please enter your name');
        return;
      }
      if (!formData.email) {
        toast.error('Please enter your email');
        return;
      }
      if (!formData.businessName) {
        toast.error('Please enter your company name');
        return;
      }
      if (!formData.phone) {
        toast.error('Please enter your phone number');
        return;
      }
    }
    
    if (currentStep < totalSteps - 1) {
      console.log('Moving to next step:', currentStep + 1);
      setCurrentStep(currentStep + 1);
    } else {
      console.log('Already on last step, not advancing');
    }
  };

  const handlePrevious = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('handlePrevious called, currentStep:', currentStep);
    
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };


const handleSubmit = async (e) => {
  console.log("handleSubmit called, currentStep:", currentStep, "totalSteps:", totalSteps);

  e.preventDefault();
  e.stopPropagation();

  // Only allow submit on final step
  if (currentStep !== totalSteps - 1) {
    console.log("Form submission prevented - not on final step");
    return;
  }

  const payload = {
    ...formData,
    addOns,
    totalPrice
  };

  console.log("Submitting payload:", payload);

  try {
    setIsSubmitting(true);

    const response = await fetch("https://itmunch.com/website-development-services/send-mail.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });

    // Check server response
    if (!response.ok) {
      throw new Error("Server returned an error");
    }

    const result = await response.json();
    console.log("Server response:", result);

    if (result.success) {
      setShowThankYou(true);
      toast.success("Form submitted successfully!");
    } else {
      toast.error("Something went wrong. Please try again.");
      return;
    }

  } catch (error) {
    console.error("Submission error:", error);
    toast.error("Server error. Please try again later.");
    return;
  } finally {
    setIsSubmitting(false);
  }

  // Reset form after success
  setTimeout(() => {
    setShowThankYou(false);
    setCurrentStep(0);

    setFormData({
      name: '',
      email: '',
      phone: '',
      countryCode: '+1',
      businessName: '',
      websiteType: '',
      hasDomain: '',
      hasHosting: '',
      hasContent: '',
      needsLogo: '',
      contactFormType: 'contact',
      needsEcommerce: '',
      needsMaintenance: '',
      referenceUrl: ''
    });

    setAddOns({
      domain: false,
      hosting: false,
      content: false,
      logo: false,
      bookingForm: false,
      ecommerce: false,
      maintenance: false
    });

  }, 3000);
};

  const scrollToForm = () => {
    document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="site-header">
        <div className="header-container">
          <div className="logo">
            <img src="https://customer-assets.emergentagent.com/job_site-creators-2/artifacts/hjfgrj1y_iTMunch%20B2B%20Content%20Syndication%20Platform%20and%20Vendor.jpeg" alt="iTMunch" />
          </div>
          <nav className="main-nav">
            <a href="https://itmunch.com/" target="_blank" rel="noopener noreferrer">Home</a>
            <a href="#services">Services</a>
            <a href="#process">Process</a>
            <a href="#why-us">Why Us</a>
            <a href="#testimonials">Testimonials</a>
            <a href="#faq">FAQ</a>
          </nav>
          <Button onClick={scrollToForm} className="header-cta">Get Started</Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section-itmunch-animated">
        {/* Animated Shader Background */}
        <ShaderBackground />
        
        {/* Gradient Overlay for better text readability */}
        <div className="hero-gradient-overlay"></div>

        <div className="hero-container">
          <div className="hero-left">
            {/* Price & Delivery - FIRST thing users see */}
            <div className="hero-price-highlight-top">
              <div className="price-badge-large">
                <span className="price-label">Starting at Only</span>
                <span className="price-amount-large">$199</span>
              </div>
              <div className="delivery-badge-large">
                <span className="delivery-icon">⚡</span>
                <span className="delivery-text-large">Delivered in Just 7 Days</span>
              </div>
            </div>

            <h1 className="hero-heading">
              Leading Website Development Service Provider
            </h1>
            
            <p className="hero-description">
              Get your business online with our expert website development services. From simple landing pages to full business websites - professional results at an affordable price.
            </p>

            <div className="hero-image-wrapper">
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Professional Website Development"
                className="hero-img"
              />
            </div>
            
            <a href="https://calendly.com/simon-p-itmunch/30min" target="_blank" rel="noopener noreferrer" className="hero-cta-btn">Book a Call →</a>
          </div>

          <div className="hero-right" id="lead-form">
            <div className="form-card-enhanced">
              <div className="form-header">
                <div className="form-progress-enhanced">
                  <div className="progress-bar-enhanced">
                    <div className="progress-fill-enhanced" style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}></div>
                  </div>
                  <p className="progress-text-enhanced">Step {currentStep + 1} of {totalSteps}</p>
                </div>
              </div>
              
              <h3 className="form-title-enhanced">Start Your Website Journey</h3>
              <p className="form-subtitle">Tell us about your project and we'll get you started</p>
              <form onSubmit={handleSubmit} className="lead-form-itmunch-enhanced" onKeyDown={(e) => {
                // Prevent form submission on Enter key press
                if (e.key === 'Enter' && e.target.tagName !== 'BUTTON') {
                  e.preventDefault();
                }
              }}>
                <div className="form-step-content-enhanced">
                  {/* Step 0: Basic Info - All 4 fields together */}
                  {currentStep === 0 && (
                    <div className="step-content-wrapper">
                      <div className="form-field-enhanced">
                        <Label htmlFor="name" className="label-enhanced">Full Name *</Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="John Doe"
                          className="input-enhanced"
                          autoFocus
                        />
                      </div>

                      <div className="form-field-enhanced">
                        <Label htmlFor="email" className="label-enhanced">Business Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="you@company.com"
                          className="input-enhanced"
                        />
                      </div>

                      <div className="form-field-enhanced">
                        <Label htmlFor="businessName" className="label-enhanced">Company Name *</Label>
                        <Input
                          id="businessName"
                          type="text"
                          value={formData.businessName}
                          onChange={(e) => handleInputChange('businessName', e.target.value)}
                          placeholder="Your Company Inc."
                          className="input-enhanced"
                        />
                      </div>

                      <div className="form-field-enhanced">
                        <Label htmlFor="phone" className="label-enhanced">Phone Number *</Label>
                        <div className="phone-input-group-enhanced">
                          <Popover open={countryCodeOpen} onOpenChange={setCountryCodeOpen}>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="country-code-button-enhanced"
                                aria-expanded={countryCodeOpen}
                              >
                                <span>{formData.countryCode}</span>
                                <ChevronDown className="chevron-icon-enhanced" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="country-code-popover-enhanced" align="start">
                              <Command>
                                <CommandInput placeholder="Search country..." className="command-input-enhanced" />
                                <CommandList>
                                  <CommandEmpty>No country found.</CommandEmpty>
                                  <CommandGroup>
                                    {countryCodes.map((country, index) => (
                                      <CommandItem
                                        key={`${country.code}-${country.country}-${index}`}
                                        value={`${country.name} ${country.code}`}
                                        onSelect={() => {
                                          handleInputChange('countryCode', country.code);
                                          setCountryCodeOpen(false);
                                        }}
                                        className="command-item-enhanced"
                                      >
                                        <Check
                                          className={`check-icon-enhanced ${formData.countryCode === country.code ? 'visible' : 'hidden'}`}
                                        />
                                        <span className="country-name-enhanced">{country.name}</span>
                                        <span className="country-code-text-enhanced">{country.code}</span>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <Input
                            id="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="123-456-7890"
                            className="phone-number-input-enhanced"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 1: Website Type */}
                  {currentStep === 1 && (
                    <div className="form-field">
                      <Label htmlFor="websiteType">What type of website do you need?</Label>
                      <Select value={formData.websiteType} onValueChange={(value) => handleInputChange('websiteType', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="business">Business</SelectItem>
                          <SelectItem value="portfolio">Portfolio</SelectItem>
                          <SelectItem value="restaurant">Restaurant</SelectItem>
                          <SelectItem value="blog">Blog</SelectItem>
                          <SelectItem value="landing">Landing Page</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Step 2: Domain */}
                  {currentStep === 2 && (
                    <div className="form-field">
                      <Label>Do you already have a domain name?</Label>
                      <RadioGroup value={formData.hasDomain} onValueChange={(value) => {
                        handleInputChange('hasDomain', value);
                        setAddOns(prev => ({ ...prev, domain: value === 'no' }));
                      }}>
                        <div className="radio-item">
                          <RadioGroupItem value="yes" id="domain-yes" />
                          <Label htmlFor="domain-yes">Yes</Label>
                        </div>
                        <div className="radio-item">
                          <RadioGroupItem value="no" id="domain-no" />
                          <Label htmlFor="domain-no">No — We can help you get one for $15/year</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Step 3: Hosting */}
                  {currentStep === 3 && (
                    <div className="form-field">
                      <Label>Do you have hosting for your website?</Label>
                      <RadioGroup value={formData.hasHosting} onValueChange={(value) => {
                        handleInputChange('hasHosting', value);
                        setAddOns(prev => ({ ...prev, hosting: value === 'no' }));
                      }}>
                        <div className="radio-item">
                          <RadioGroupItem value="yes" id="hosting-yes" />
                          <Label htmlFor="hosting-yes">Yes</Label>
                        </div>
                        <div className="radio-item">
                          <RadioGroupItem value="no" id="hosting-no" />
                          <Label htmlFor="hosting-no">No — We offer hosting for $10/month</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Step 4: Content */}
                  {currentStep === 4 && (
                    <div className="form-field">
                      <Label>Do you have content ready (text & images)?</Label>
                      <RadioGroup value={formData.hasContent} onValueChange={(value) => {
                        handleInputChange('hasContent', value);
                        setAddOns(prev => ({ ...prev, content: value === 'no' }));
                      }}>
                        <div className="radio-item">
                          <RadioGroupItem value="yes" id="content-yes" />
                          <Label htmlFor="content-yes">Yes, I'll provide everything</Label>
                        </div>
                        <div className="radio-item">
                          <RadioGroupItem value="no" id="content-no" />
                          <Label htmlFor="content-no">No — We can create basic content for $10 (for upto 10 pages)</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Step 5: Logo */}
                  {currentStep === 5 && (
                    <div className="form-field">
                      <Label>Do you need a logo?</Label>
                      <RadioGroup value={formData.needsLogo} onValueChange={(value) => {
                        handleInputChange('needsLogo', value);
                        setAddOns(prev => ({ ...prev, logo: value === 'yes' }));
                      }}>
                        <div className="radio-item">
                          <RadioGroupItem value="no" id="logo-no" />
                          <Label htmlFor="logo-no">No</Label>
                        </div>
                        <div className="radio-item">
                          <RadioGroupItem value="yes" id="logo-yes" />
                          <Label htmlFor="logo-yes">Yes — Add a custom logo for just $29</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Step 6: Contact Form */}
                  {currentStep === 6 && (
                    <div className="form-field">
                      <Label>Do you need a contact form or booking feature?</Label>
                      <RadioGroup value={formData.contactFormType} onValueChange={(value) => {
                        handleInputChange('contactFormType', value);
                        setAddOns(prev => ({ ...prev, bookingForm: value === 'booking' }));
                      }}>
                        <div className="radio-item">
                          <RadioGroupItem value="contact" id="form-contact" />
                          <Label htmlFor="form-contact">Contact form only (Included)</Label>
                        </div>
                        <div className="radio-item">
                          <RadioGroupItem value="booking" id="form-booking" />
                          <Label htmlFor="form-booking">Booking form — Add for $15 extra</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Step 7: eCommerce */}
                  {currentStep === 7 && (
                    <div className="form-field">
                      <Label>Do you need eCommerce functionality?</Label>
                      <RadioGroup value={formData.needsEcommerce} onValueChange={(value) => {
                        handleInputChange('needsEcommerce', value);
                        setAddOns(prev => ({ ...prev, ecommerce: value === 'yes' }));
                      }}>
                        <div className="radio-item">
                          <RadioGroupItem value="no" id="ecommerce-no" />
                          <Label htmlFor="ecommerce-no">No</Label>
                        </div>
                        <div className="radio-item">
                          <RadioGroupItem value="yes" id="ecommerce-yes" />
                          <Label htmlFor="ecommerce-yes">Yes — We offer product page setup starting at $99/page</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Step 8: Maintenance */}
                  {currentStep === 8 && (
                    <div className="form-field">
                      <Label>Would you like monthly maintenance & updates?</Label>
                      <RadioGroup value={formData.needsMaintenance} onValueChange={(value) => {
                        handleInputChange('needsMaintenance', value);
                        setAddOns(prev => ({ ...prev, maintenance: value === 'yes' }));
                      }}>
                        <div className="radio-item">
                          <RadioGroupItem value="no" id="maintenance-no" />
                          <Label htmlFor="maintenance-no">No thanks</Label>
                        </div>
                        <div className="radio-item">
                          <RadioGroupItem value="yes" id="maintenance-yes" />
                          <Label htmlFor="maintenance-yes">Yes — Only $10/month to keep your site updated, secure & fast</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  )}

                  {/* Step 9: Reference URL + Price Summary */}
                  {currentStep === 9 && (
                    <div className="form-field">
                      <Label htmlFor="referenceUrl">Any reference sites or inspiration?</Label>
                      <Textarea
                        id="referenceUrl"
                        value={formData.referenceUrl}
                        onChange={(e) => handleInputChange('referenceUrl', e.target.value)}
                        placeholder="Enter website URLs or describe your vision..."
                        rows={4}
                      />
                      
                      {/* Price Summary */}
                      <div className="price-summary-box" style={{ marginTop: '1.5rem' }}>
                        <div className="price-line">
                          <span>Base Website</span>
                          <span>${pricing.base}</span>
                        </div>
                        {addOns.domain && (
                          <div className="price-line addon">
                            <span>Domain Registration</span>
                            <span>+${pricing.domain}</span>
                          </div>
                        )}
                        {addOns.hosting && (
                          <div className="price-line addon">
                            <span>Hosting (monthly)</span>
                            <span>+${pricing.hosting}</span>
                          </div>
                        )}
                        {addOns.content && (
                          <div className="price-line addon">
                            <span>Content Creation</span>
                            <span>+${pricing.content}</span>
                          </div>
                        )}
                        {addOns.logo && (
                          <div className="price-line addon">
                            <span>Custom Logo</span>
                            <span>+${pricing.logo}</span>
                          </div>
                        )}
                        {addOns.bookingForm && (
                          <div className="price-line addon">
                            <span>Booking Form</span>
                            <span>+${pricing.bookingForm}</span>
                          </div>
                        )}
                        {addOns.ecommerce && (
                          <div className="price-line addon">
                            <span>eCommerce Setup</span>
                            <span>+${pricing.ecommerce}</span>
                          </div>
                        )}
                        {addOns.maintenance && (
                          <div className="price-line addon">
                            <span>Monthly Maintenance</span>
                            <span>+${pricing.maintenance}/mo</span>
                          </div>
                        )}
                        <div className="price-line total">
                          <span>Total</span>
                          <span>${totalPrice}</span>
                        </div>
                      </div>

                      <p className="privacy-text" style={{ marginTop: '1rem' }}>
                        By submitting this form, you agree to our Privacy Policy.
                      </p>
                    </div>
                  )}
                </div>

                {/* Navigation Buttons */}
                <div className="form-navigation">
                  {currentStep > 0 && (
                    <Button type="button" onClick={handlePrevious} className="btn-previous">
                      Previous
                    </Button>
                  )}
                  {currentStep < totalSteps - 1 ? (
                    <Button type="button" onClick={handleNext} className="btn-next">
                      Next
                    </Button>
                  ) : (
                    <Button type="submit" disabled={isSubmitting} className="form-submit-btn">
  {isSubmitting ? "Submitting..." : "Get Started Now"}
</Button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-number">500+</div>
            <div className="stat-label">Successful Projects</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">200+</div>
            <div className="stat-label">Happy Clients</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">50+</div>
            <div className="stat-label">Industry Experts</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">7 Days</div>
            <div className="stat-label">Average Delivery</div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="services" className="benefits-section">
        <div className="section-container">
          <h2 className="section-heading">Benefits of Our Website Development Services</h2>
          <p className="section-subheading">Partner with leading experts to unlock powerful growth opportunities</p>
          
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <Zap size={40} />
              </div>
              <h3 className="benefit-title">Fast Turnaround</h3>
              <p className="benefit-description">
                Get your website live in just 7 days. We prioritize efficiency without compromising quality, delivering professional websites rapidly.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <Target size={40} />
              </div>
              <h3 className="benefit-title">Custom Solutions</h3>
              <p className="benefit-description">
                Every business is unique. We create tailored websites that perfectly match your brand identity and business objectives.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <Users size={40} />
              </div>
              <h3 className="benefit-title">Expert Team</h3>
              <p className="benefit-description">
                Work with seasoned professionals who understand modern web development, design trends, and user experience best practices.
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <Shield size={40} />
              </div>
              <h3 className="benefit-title">Secure & Reliable</h3>
              <p className="benefit-description">
                Built with security in mind. Your website will be hosted on reliable infrastructure with regular backups and updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="process-section">
        <div className="section-container">
          <h2 className="section-heading">How Our Website Development Process Works</h2>
          <p className="section-subheading">A proven 5-step methodology for delivering exceptional websites</p>
          
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">01</div>
              <h3 className="step-title">Discovery & Planning</h3>
              <p className="step-description">
                We start by understanding your business goals, target audience, and specific requirements. This ensures we build exactly what you need.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">02</div>
              <h3 className="step-title">Design & Development</h3>
              <p className="step-description">
                Our design team creates a beautiful, modern interface while developers build a robust, responsive website optimized for all devices.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">03</div>
              <h3 className="step-title">Content Integration</h3>
              <p className="step-description">
                We integrate your content, images, and branding elements. If needed, our content team can create compelling copy for your pages.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">04</div>
              <h3 className="step-title">Testing & Quality Assurance</h3>
              <p className="step-description">
                Rigorous testing ensures your website works flawlessly across browsers, devices, and screen sizes before launch.
              </p>
            </div>

            <div className="process-step">
              <div className="step-number">05</div>
              <h3 className="step-title">Launch & Support</h3>
              <p className="step-description">
                We handle the technical launch process and provide ongoing support to ensure your website continues to perform optimally.
              </p>
            </div>
          </div>
        </div>

        <div className="process-image-wrapper">
          <img 
            src="https://images.unsplash.com/photo-1601509876296-aba16d4c10a4"
            alt="Professional Website Development Team"
            className="process-img"
          />
        </div>
      </section>

      {/* Why Choose Section */}
      <section id="why-us" className="why-choose-section">
        <div className="section-container">
          <h2 className="section-heading">Why Choose iTMunch for Your Website?</h2>
          <p className="section-subheading">Unmatched expertise and results-driven approach to web development</p>
          
          <div className="why-choose-grid">
            <div className="why-card">
              <div className="why-icon">
                <TrendingUp size={36} />
              </div>
              <h3 className="why-title">Proven Track Record</h3>
              <p className="why-description">
                Over 500 successful projects delivered to businesses across various industries. Our portfolio speaks for our quality and expertise.
              </p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <CheckCircle2 size={36} />
              </div>
              <h3 className="why-title">All-Inclusive Packages</h3>
              <p className="why-description">
                From domain registration to hosting, design to development, and maintenance - we handle everything so you don't have to worry.
              </p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <Zap size={36} />
              </div>
              <h3 className="why-title">Rapid Deployment</h3>
              <p className="why-description">
                We value your time. Our streamlined process ensures your website goes live in just 7 days without sacrificing quality.
              </p>
            </div>

            <div className="why-card">
              <div className="why-icon">
                <Shield size={36} />
              </div>
              <h3 className="why-title">Dedicated Support</h3>
              <p className="why-description">
                Our relationship doesn't end at launch. Get ongoing support, maintenance, and updates to keep your website running smoothly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="testimonials-section">
        <div className="section-container">
          <h2 className="section-heading">What Our Clients Say</h2>
          <p className="section-subheading">Trusted by business leaders worldwide</p>
          
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <p className="testimonial-text">
                "iTMunch delivered our website ahead of schedule. The quality exceeded our expectations, and the team was incredibly professional throughout the process."
              </p>
              <div className="testimonial-author">
                <span>CEO</span>
                <span>Technology Industry</span>
              </div>
            </div>

            <div className="testimonial-card">
              <p className="testimonial-text">
                "The affordability combined with the quality of work is unmatched. We got a professional website that truly represents our brand for a fraction of what we expected to pay."
              </p>
              <div className="testimonial-author">
                <span>Founder</span>
                <span>Digital Solutions Industry</span>
              </div>
            </div>

            <div className="testimonial-card">
              <p className="testimonial-text">
                "Working with iTMunch was seamless. They understood our vision and brought it to life. The ongoing support has been fantastic as well."
              </p>
              <div className="testimonial-author">
                <span>Marketing Director</span>
                <span>Business Growth Industry</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="section-container">
          <h2 className="section-heading">Frequently Asked Questions</h2>
          <p className="section-subheading">Everything you need to know about our website development services</p>
          
          <div className="faq-wrapper">
            <Accordion type="single" collapsible className="faq-accordion">
              <AccordionItem value="item-1">
                <AccordionTrigger>What is included in the $199 base package?</AccordionTrigger>
                <AccordionContent>
                  The base package includes a professionally designed website with up to 5 pages, responsive design for all devices, basic SEO optimization, contact form integration, and 30 days of post-launch support.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2">
                <AccordionTrigger>How long does it take to build a website?</AccordionTrigger>
                <AccordionContent>
                  Our standard turnaround time is 7 days from the moment we receive all required content and information. For more complex projects with e-commerce or custom features, the timeline may extend to 10-14 days.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3">
                <AccordionTrigger>Do I need technical knowledge to manage my website?</AccordionTrigger>
                <AccordionContent>
                  Not at all! We build user-friendly websites with easy-to-use content management systems. We also provide training and documentation to help you update your website content independently. Plus, our support team is always available if you need assistance.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4">
                <AccordionTrigger>Can I upgrade my website later with additional features?</AccordionTrigger>
                <AccordionContent>
                  Absolutely! We design websites with scalability in mind. You can add e-commerce functionality, booking systems, custom integrations, or any other features at any time. We offer flexible upgrade packages to meet your growing business needs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5">
                <AccordionTrigger>What if I don't have content or images ready?</AccordionTrigger>
                <AccordionContent>
                  No problem! We offer content creation services for just $10 (covering up to 10 pages). Our content team can write compelling copy for your website, and we have access to professional stock images to make your site look amazing.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6">
                <AccordionTrigger>Do you provide ongoing maintenance and support?</AccordionTrigger>
                <AccordionContent>
                  Yes! We offer monthly maintenance packages starting at $10/month. This includes regular security updates, backups, performance optimization, and technical support to ensure your website stays fast, secure, and up-to-date.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2 className="cta-heading">Ready to Get Your Business Online?</h2>
          <p className="cta-text">
            Partner with iTMunch and start your website journey today. Professional websites starting at just $199.
          </p>
          <div className="cta-buttons">
            <Button onClick={scrollToForm} className="cta-primary">Get Started Now →</Button>
            <Button onClick={scrollToForm} className="cta-secondary">Schedule a Call</Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-column footer-about">
            <img src="https://itmunch.com/wp-content/uploads/2021/01/ITT-MUNCH_LOGO-4-1-1024x512_800x400.webp" alt="iTMunch" className="footer-logo" />
            <p className="footer-description">
              iTMunch matches you with top firms for website development, delivering professional results at affordable prices.
            </p>
            <div className="footer-social">
              <a href="https://facebook.com/itmunch" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://twitter.com/itmunch" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Twitter">
      <span style={{ fontFamily: 'Arial, sans-serif', fontSize: '20px', color: 'currentColor' }}>
            𝕏
      </span>
              </a>
              <a href="https://linkedin.com/company/itmunch" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="https://instagram.com/itmunch" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
                </svg>
              </a>
              <a href="https://youtube.com/@itmunch" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="YouTube">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>

          <div className="footer-column">
            <h4 className="footer-heading">Services</h4>
            <ul className="footer-links">
              <li><a href="https://itmunch.com/b2b-content-syndication-services/" target="_blank" rel="noopener noreferrer">B2B Content Syndication</a></li>
              <li><a href="https://itmunch.com/b2b-lead-generation-services/" target="_blank" rel="noopener noreferrer">B2B Lead Generation</a></li>
              <li><a href="https://itmunch.com/email-marketing-services/" target="_blank" rel="noopener noreferrer">E-mail Marketing Services</a></li>
              <li><a href="https://itmunch.com/enterprise-seo-services/" target="_blank" rel="noopener noreferrer">Enterprise SEO Services</a></li>
              <li><a href="https://itmunch.com/meta-advertising-services/" target="_blank" rel="noopener noreferrer">Meta Advertising Services</a></li>
              <li><a href="https://itmunch.com/programmatic-advertising-services/" target="_blank" rel="noopener noreferrer">Programmatic Advertising</a></li>
              <li><a href="https://itmunch.com/ai-agent-for-sales/" target="_blank" rel="noopener noreferrer">AI Agent Services</a></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4 className="footer-heading">Company</h4>
            <ul className="footer-links">
              <li><a href="#why-us">About Us</a></li>
              <li><a href="#testimonials">Testimonials</a></li>
              <li><a href="#faq">FAQ</a></li>
              <li><a href="#process">Our Process</a></li>
              <li><a href="https://itmunch.com/" target="_blank" rel="noopener noreferrer">Blog</a></li>
              <li><a href="https://itmunch.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
            </ul>
          </div>

          <div className="footer-column">
            <h4 className="footer-heading">Contact Us</h4>
            <ul className="footer-contact">
              <li>
                <Mail size={16} />
                <a href="mailto:connect@itmunch.com">connect@itmunch.com</a>
              </li>
              <li>
                <Phone size={16} />
                <a href="tel:+14153437122">(415) 343-7122</a>
              </li>
              <li>
                <MapPin size={16} />
                <span>555 Republic Dr Ste 200<br />Plano, Texas 75074<br />United States</span>
              </li>
            </ul>
            <a href="https://calendly.com/simon-p-itmunch/30min" target="_blank" rel="noopener noreferrer" className="footer-cta-btn">Book a Call</a>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 iTMunch. All rights reserved.</p>
        </div>
      </footer>

      {/* Thank You Modal */}
      {showThankYou && (
        <div className="thank-you-modal">
          <div className="thank-you-content">
            <CheckCircle2 size={64} className="thank-you-icon" />
            <h2>THANK YOU!</h2>
            <p>Your message has been successfully submitted. We appreciate your inquiry and will get back to you as soon as possible.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;